import useSWR from 'swr';
import { AccountTypeNumber } from '~/types';
import { AccountModel } from '../../../views/Accounts';
import internalApiClient from '../../util/api/internalApiClient';

/**
 * Account types that should be hidden by default unless explicitly shown
 */
const ACCOUNT_TYPES_HIDDEN_BY_DEFAULT: AccountTypeNumber[] = [
    AccountTypeNumber.TotalFrom,
    AccountTypeNumber.TotalIntervals,
    AccountTypeNumber.SumAlpha,
];

/**
 * Types that represent heading accounts in the system
 */
const HEADING_ACCOUNT_TYPES: AccountTypeNumber[] = [AccountTypeNumber.Heading, AccountTypeNumber.HeadingBreak];

interface AccountFilterOptions {
    showAllAccounts?: boolean;
    showAllAccountTypes?: boolean;
    showHeadingAccounts?: boolean;
    isBlockedForDirectEntries?: boolean;
    accountTypesToHide?: AccountTypeNumber[];
}

/**
 * Determines which account types should be hidden based on filter options
 */
const getAccountTypesToHide = ({
    showAllAccountTypes,
    showHeadingAccounts,
    accountTypesToHide = [],
}: AccountFilterOptions): AccountTypeNumber[] => {
    if (showAllAccountTypes) return accountTypesToHide;

    const typesToHide = [...accountTypesToHide];

    if (!showHeadingAccounts) {
        typesToHide.push(...HEADING_ACCOUNT_TYPES);
    }

    typesToHide.push(...ACCOUNT_TYPES_HIDDEN_BY_DEFAULT);

    return typesToHide;
};

/**
 * Checks if an account is accessible based on its type and access settings
 * Returns true for heading accounts even if they're marked as inaccessible
 */
const isAccountAccessible = (account: AccountModel): boolean => {
    if (!account.type) return false;
    if (account.access) return true;
    return HEADING_ACCOUNT_TYPES.includes(account.type.number);
};

/**
 * Filters accounts based on provided options
 * Handles visibility rules for different account types and access levels
 */
const filterAccounts = (accounts: AccountModel[] = [], options: AccountFilterOptions): AccountModel[] => {
    if (!Array.isArray(accounts)) return [];
    if (options.showAllAccounts) return accounts;

    const typesToHide = getAccountTypesToHide(options);

    return accounts.filter(account => {
        // Check account accessibility
        if (!isAccountAccessible(account)) return false;

        // Filter by account type
        if (typesToHide.includes(account.type.number)) return false;

        // Apply direct entry restrictions if specified
        if (options.isBlockedForDirectEntries !== undefined) {
            return account.isBlockedForDirectEntries === options.isBlockedForDirectEntries;
        }

        return true;
    });
};

const fetcher = async (url: string) => {
    const response = await internalApiClient.get(url);
    return Array.isArray(response.data) ? response.data : [];
};

/**
 * Custom hook for fetching and filtering accounts
 * Uses SWR for data fetching with manual revalidation control
 */
function useAccountsSwr(filterOptions: AccountFilterOptions, { skip = false } = {}) {
    const {
        data: accounts,
        isLoading,
        mutate,
    } = useSWR<AccountModel[]>(!skip ? '/accounts' : null, fetcher, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
    });

    return {
        data: filterAccounts(accounts, filterOptions),
        loading: isLoading,
        mutate,
    };
}

export default useAccountsSwr;
